import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import Modal from '@/components/common/modal';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useEventStore } from '@/state/stores/use-event-store';
import { useNotifications } from '@/hooks/use-notifications';
import { useAuth } from '@clerk/clerk-react';
import { useAddEventSlotMutation } from '@/state/mutations/events/use-add-event-slot';
import TimePicker from '@/components/common/time-picker';
import { addDays, format, parse } from 'date-fns';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { getObjectId } from '@/helpers/object.id.helper';

const addEventSlotSchema = z.object({
  eventDayId: z.string().min(1, 'Event day is required'),
  startTime: z
    .string()
    .min(1, 'Start time is required')
    .regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Start time must be in HH:mm format'),
  endTime: z
    .string()
    .regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'End time must be in HH:mm format')
    .optional(),
});

type AddEventSlotSchema = z.infer<typeof addEventSlotSchema>;

const FALLBACK_START_TIME = '08:00';

export const AddEventSlotModal = ({
  open,
  setOpen,
  flow,
}: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  flow: FlowDto;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const { userId } = useAuth();

  const event = useEventStore((s) => s.event);
  const { mutateAsync: addEventSlotMutation } = useAddEventSlotMutation();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<AddEventSlotSchema>({
    resolver: zodResolver(addEventSlotSchema),
    defaultValues: {
      eventDayId: '', // Empty string matches Select's disabled MenuItem value and schema type
      startTime: FALLBACK_START_TIME,
      endTime: undefined,
    },
  });

  useEffect(() => {
    if (event?.days?.[0]?.id) {
      const day = event?.days?.[0];
      reset({
        eventDayId: day.id,
        startTime: minuteOffsetToTime(day.startOffsetMinutes),
        endTime: undefined,
      });
    }
  }, [event?.days, reset]);

  const onSubmit = async (values: AddEventSlotSchema) => {
    if (!event?.id || !userId || !flow) {
      notification.notifyError(t('event.detail.addFlow.notifications.addFlowFailed'));
      return;
    }

    try {
      const flowId = flow.origin ? getObjectId(flow.origin) : flow.id;

      await addEventSlotMutation({
        eventId: event.id,
        flowId,
        eventDayId: values.eventDayId,
        startTime: values.startTime,
        endTime: values.endTime,
      });

      notification.notifySuccess(
        t(
          'event.detail.addFlow.notifications.addFlowSuccess' as TranslationKey,
          undefined,
          'Event slot added successfully',
        ),
      );

      setOpen(false);
      reset();
    } catch (error) {
      console.error('Error adding event slot:', error);
      notification.notifyError(
        t('event.detail.addFlow.notifications.addFlowFailed' as TranslationKey, undefined, 'Failed to add event slot'),
      );
    }
  };

  const flowDay = useWatch({ control, name: 'eventDayId', defaultValue: '' });
  useEffect(() => {
    if (flowDay) {
      const day = event?.days.find((day) => day.id === flowDay);
      if (day) {
        setValue('startTime', minuteOffsetToTime(day.startOffsetMinutes));
        setValue('endTime', undefined);
      }
    }
  }, [flowDay, event?.days, setValue]);

  return (
    <Modal
      open={open}
      data-testid="add-event-slot-modal"
      handleClose={() => setOpen(false)}
      title={t('flows.addEventSlot.title' as TranslationKey, undefined, 'Add Event Slot')}
      description={t(
        'flows.addEventSlot.description' as TranslationKey,
        undefined,
        'Add another scheduled slot for this flow',
      )}
      sx={{ maxWidth: '350px' }}
      contentSx={{ pt: 1 }}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outlined" color="info" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            data-testid="add-event-slot-button"
            variant="contained"
            color="info"
            onClick={handleSubmit(onSubmit)}
          >
            {t('common.save')}
          </Button>
        </Stack>
      }
    >
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={2} sx={{ width: '100%' }}>
        <Controller
          name="eventDayId"
          control={control}
          render={({ field }) => (
            <Box>
              <FormControl fullWidth variant="standard" error={!!errors.eventDayId}>
                <InputLabel>{`${t('event.detail.addFlow.values.eventDay.label')} *`}</InputLabel>
                <Select {...field} data-testid="day-select">
                  <MenuItem value="" disabled>
                    {t('event.detail.addFlow.values.eventDay.description')}
                  </MenuItem>
                  {event?.days.map((day, i) => (
                    <MenuItem key={`day-${i}`} value={day.id}>
                      {t('event.detail.addFlow.values.eventDay.value', {
                        dayId: `${i + 1}`,
                        date: format(addDays(event.startDate, i), 'dd/MM/yyyy'),
                      })}
                    </MenuItem>
                  ))}
                </Select>
                {errors.eventDayId && (
                  <Typography variant="caption" color="error">
                    {errors.eventDayId.message}
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}
        />
        <Stack direction="row" spacing={1} alignItems="flex-start" width="100%">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              name="startTime"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TimePicker
                  label={`${t('event.detail.addFlow.values.startTime')} *`}
                  name="startTime"
                  views={['hours', 'minutes']}
                  value={value ? parse(value, 'HH:mm', new Date()) : null}
                  onChange={(value) => {
                    if (value) {
                      onChange(format(value, 'HH:mm'));
                    } else {
                      onChange(null);
                    }
                  }}
                  slotProps={{
                    textField: {
                      variant: 'standard',
                      required: true,
                      error: !!errors.startTime,
                      helperText: errors.startTime?.message,
                      fullWidth: true,
                      sx: { minWidth: 0 },
                    },
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              name="endTime"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TimePicker
                  label={t('event.detail.addFlow.values.endTime')}
                  name="endTime"
                  views={['hours', 'minutes']}
                  value={value ? parse(value, 'HH:mm', new Date()) : null}
                  onChange={(value) => {
                    if (value) {
                      onChange(format(value, 'HH:mm'));
                    } else {
                      onChange(undefined);
                    }
                  }}
                  slotProps={{
                    textField: {
                      variant: 'standard',
                      required: false,
                      error: !!errors.endTime,
                      helperText: errors.endTime?.message,
                      fullWidth: true,
                      sx: { minWidth: 0 },
                      placeholder: 'flexible',
                    },
                  }}
                />
              )}
            />
          </Box>
        </Stack>
      </Stack>
    </Modal>
  );
};
